/**
 * MyMilestonesCard Component
 *
 * Employee view - Shows personal milestones and objectives
 */

import React from 'react';
import { Box, Typography, Button, Chip, IconButton, Tooltip } from '@mui/material';
import { Flag, Add, Edit } from '@mui/icons-material';
import { useOKRDashboardContext } from '../OKRDashboardContext';
import { CollapsibleCard } from '../shared/CollapsibleCard';
import { EmptyState } from '../shared/EmptyState';
import { MilestoneItem } from '../shared/MilestoneItem';
import { getScoreColor, getScoreLabel } from '../utils/scoreUtils';
import { okrApi } from '../../../services/okrApi';
import { Milestone } from '../../../types';

export const MyMilestonesCard: React.FC = () => {
    const { isDarkMode, data, permissions, sections, dialogs, filteredObjectives } = useOKRDashboardContext();
    const { isLocked, userContext } = data;
    const { expandedSections, toggleSection } = sections;

    // Show for employees (non-admin, non-manager)
    if (permissions.isAdmin || permissions.isHR) {
        return null;
    }

    const myObjectives = filteredObjectives.individual;

    const handleMilestoneStatusChange = async (milestone: Milestone, newStatus: string) => {
        try {
            await okrApi.updateMilestoneStatus(milestone.id, newStatus);
            // Soft refresh to update scores throughout the hierarchy
            if (data.softRefetchData) {
                await data.softRefetchData();
            }
        } catch (error: any) {
            console.error('Error updating milestone status:', error);
            data.setError(error.message || 'Failed to update milestone status');
        }
    };

    const handleEditMilestone = (milestone: Milestone) => {
        dialogs.openEditMilestoneDialog(milestone);
    };

    const handleDeleteMilestone = async (milestone: Milestone) => {
        if (window.confirm('Are you sure you want to delete this milestone?')) {
            try {
                await okrApi.deleteMilestone(milestone.id);
                data.setSuccess('Milestone deleted successfully');
                if (data.softRefetchData) {
                    await data.softRefetchData();
                }
            } catch (error: any) {
                console.error('Error deleting milestone:', error);
                data.setError(error.message || 'Failed to delete milestone');
            }
        }
    };

    return (
        <CollapsibleCard
            icon={<Flag sx={{ color: '#fff', fontSize: 24 }} />}
            iconBgGradient="linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)"
            title="My Objectives & Milestones"
            subtitle="Your assigned objectives and progress"
            expanded={expandedSections.execution}
            onToggle={() => toggleSection('execution')}
            isDarkMode={isDarkMode}
            count={myObjectives.length}
            actions={
                !isLocked && permissions.canCreateIndividualOKR && (
                    <Button
                        size="small"
                        startIcon={<Add />}
                        onClick={(e) => {
                            e.stopPropagation();
                            // Open create individual objective dialog
                            if (filteredObjectives.team[0]?.id) {
                                dialogs.openCreateIndObjDialog(filteredObjectives.team[0].id);
                            }
                        }}
                        sx={{
                            bgcolor: 'rgba(139, 92, 246, 0.15)',
                            color: '#8b5cf6',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            borderRadius: 2,
                            textTransform: 'none',
                            '&:hover': {
                                bgcolor: 'rgba(139, 92, 246, 0.25)',
                            },
                        }}
                    >
                        Add Objective
                    </Button>
                )
            }
        >
            {myObjectives.length === 0 ? (
                <EmptyState
                    icon={<Flag sx={{ fontSize: 48, color: isDarkMode ? '#64748b' : '#94a3b8' }} />}
                    title="No Objectives Yet"
                    description="Your assigned objectives will appear here"
                    isDarkMode={isDarkMode}
                    compact
                />
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {myObjectives.map((obj: any) => {
                        const scoreColor = getScoreColor(obj.score || 0);
                        const milestones = obj.milestones || [];

                        return (
                            <Box
                                key={obj.id}
                                sx={{
                                    p: 3,
                                    borderRadius: 3,
                                    bgcolor: isDarkMode ? '#1e293b40' : '#f8fafc',
                                    border: `1px solid ${isDarkMode ? '#334155' : '#e2e8f0'}`,
                                }}
                            >
                                {/* Objective Header */}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Box
                                            sx={{
                                                width: 44,
                                                height: 44,
                                                borderRadius: 2,
                                                bgcolor: `${scoreColor}15`,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                border: `2px solid ${scoreColor}30`,
                                            }}
                                        >
                                            <Flag sx={{ color: scoreColor, fontSize: 24 }} />
                                        </Box>
                                        <Box>
                                            <Typography
                                                sx={{
                                                    fontWeight: 700,
                                                    fontSize: '1rem',
                                                    color: isDarkMode ? '#f1f5f9' : '#1e293b',
                                                }}
                                            >
                                                {obj.title}
                                            </Typography>
                                            {obj.team_title && (
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: isDarkMode ? '#64748b' : '#94a3b8',
                                                    }}
                                                >
                                                    Linked to: {obj.team_title}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Tooltip title={`Status: ${getScoreLabel(obj.score || 0)} — Updated by manager/admin`} arrow>
                                            <Chip
                                                label={`${Math.round(obj.score || 0)}%`}
                                                size="small"
                                                sx={{
                                                    bgcolor: scoreColor,
                                                    color: '#fff',
                                                    fontWeight: 700,
                                                    fontSize: '0.8rem',
                                                    boxShadow: `0 2px 6px ${scoreColor}40`,
                                                }}
                                            />
                                        </Tooltip>
                                        {!isLocked && (
                                            (permissions.isManager || permissions.isAdmin) ? (
                                                <IconButton
                                                    size="small"
                                                    onClick={() => dialogs.openEditIndObjDialog(obj)}
                                                    sx={{
                                                        color: isDarkMode ? '#94a3b8' : '#64748b',
                                                        '&:hover': {
                                                            bgcolor: isDarkMode ? '#334155' : '#e2e8f0',
                                                        },
                                                    }}
                                                >
                                                    <Edit fontSize="small" />
                                                </IconButton>
                                            ) : (
                                                <Tooltip title="Only managers or admins can edit objectives" arrow>
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            disabled
                                                            sx={{
                                                                color: isDarkMode ? '#475569' : '#cbd5e1',
                                                            }}
                                                        >
                                                            <Edit fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            )
                                        )}
                                        {!isLocked && permissions.canEditMilestones && (
                                            <Button
                                                size="small"
                                                startIcon={<Add />}
                                                onClick={() => dialogs.openAddMilestoneDialog(obj.id, userContext?.id)}
                                                sx={{
                                                    bgcolor: 'rgba(139, 92, 246, 0.15)',
                                                    color: '#8b5cf6',
                                                    fontWeight: 600,
                                                    fontSize: '0.75rem',
                                                    textTransform: 'none',
                                                    '&:hover': {
                                                        bgcolor: 'rgba(139, 92, 246, 0.25)',
                                                    },
                                                }}
                                            >
                                                Add Milestone
                                            </Button>
                                        )}
                                    </Box>
                                </Box>

                                {/* Milestones List */}
                                {milestones.length > 0 ? (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
                                        {milestones.map((milestone: any) => (
                                            <MilestoneItem
                                                key={milestone.id}
                                                milestone={milestone}
                                                isDarkMode={isDarkMode}
                                                showActions={permissions.canEditMilestones}
                                                onStatusChange={!isLocked ? handleMilestoneStatusChange : undefined}
                                                onEdit={!isLocked ? handleEditMilestone : undefined}
                                                onDelete={!isLocked ? handleDeleteMilestone : undefined}
                                            />
                                        ))}
                                    </Box>
                                ) : (
                                    <Typography
                                        sx={{
                                            color: isDarkMode ? '#64748b' : '#94a3b8',
                                            fontSize: '0.85rem',
                                            fontStyle: 'italic',
                                            mt: 2,
                                        }}
                                    >
                                        No milestones defined yet - add milestones to track your progress
                                    </Typography>
                                )}
                            </Box>
                        );
                    })}
                </Box>
            )}
        </CollapsibleCard>
    );
};

export default MyMilestonesCard;
